import { useTranslation } from "react-i18next";
import {
  Card,
  formatDayLabel,
  formatDuration,
  formatTimeLabel,
  isSameCalendarDay,
} from "./range-selector";
import tw from "twin.macro";
import { ArrowRight, Clock, Umbrella } from "lucide-react";

export type ProtectionPeriod = {
  from: string;
  to: string;
};

export const ProtectionPeriodList = ({
  periods,
  children,
}: {
  periods: ProtectionPeriod[];
  children: React.ReactNode;
}) => {
  const { t } = useTranslation();
  return (
    <Card tw="p-4 flex flex-col gap-3">
      <p tw="text-base font-semibold text-gray-800">
        {t("meteo.climateView.protectionPeriods.title")}
      </p>
      {periods.length === 0 ? (
        <p tw="text-sm text-gray-500">
          {t("meteo.climateView.protectionPeriods.empty")}
        </p>
      ) : (
        <div tw="flex flex-col">{children}</div>
      )}
    </Card>
  );
};

export const ProtectionPeriodComponent = ({
  from,
  to,
  isLast = false,
  children,
}: {
  from: string;
  to: string;
  isLast?: boolean;
  children?: React.ReactNode;
}) => {
  // Le jour de fin n'est affiché que si la période chevauche deux jours :
  // sur une période intra-journalière il serait redondant avec le jour de début.
  // Ce cas rallonge la ligne du haut au point de passer sous le badge (elle est
  // en `whitespace-nowrap`, donc ne peut pas rétrécir) : on descend alors le
  // badge sur la ligne de la durée pour lui laisser toute la largeur.
  const showEndDay = !isSameCalendarDay(from, to);
  return (
    <div
      css={[
        tw`flex items-center gap-3 py-3`,
        !isLast && tw`border-b border-gray-100`,
      ]}
    >
      <div tw="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
        <Umbrella tw="w-5 h-5 text-emerald-600" />
      </div>
      <div tw="flex-1 min-w-0 flex flex-col gap-1">
        <div tw="flex items-center gap-1.5 text-sm min-w-0">
          <span tw="font-semibold text-gray-800 whitespace-nowrap">
            {formatDayLabel(from)}
          </span>
          <span tw="text-gray-500 whitespace-nowrap">{formatTimeLabel(from)}</span>
          <ArrowRight tw="w-3.5 h-3.5 text-gray-400 shrink-0" />
          {showEndDay && (
            <span tw="font-semibold text-gray-800 whitespace-nowrap">
              {formatDayLabel(to)}
            </span>
          )}
          <span tw="text-gray-500 whitespace-nowrap">{formatTimeLabel(to)}</span>
        </div>
        <div tw="flex items-center gap-1 text-xs text-gray-400">
          <Clock tw="w-3 h-3 shrink-0" />
          <span>{formatDuration(from, to)}</span>
          {showEndDay && children && (
            <div tw="ml-auto pl-2 shrink-0">{children}</div>
          )}
        </div>
      </div>
      {!showEndDay && children}
    </div>
  );
};
